import type { TaskViewRequiredScope } from "@verbleif/lib/src/composables/Api/Type/TaskView/taskView.interface";
import type { DefaultViewScope } from "@verbleif/lib/src/composables/Views/DefaultViewReference";
import type { TaskListScopeSelection } from "@verbleif/shared/src/composables/TaskListScope/taskListScopeTree";
import type { TaskViewScopeParams } from "@web/features/Tasks/Views/taskViewScopeQuery";
import { TASK_VIEW_SCOPE_QUERY } from "@web/features/Tasks/Views/taskViewScopeQuery";

/**
 * The choice of a view that asks for one (view management on): an object for Per object, a walk
 * route for Looproute, and for Per afdeling a department, a user inside it, or a user without a
 * department. It lives in the URL only; nothing is stored except a configured default.
 */
export type TaskViewChoice
  = | { type: "taskList", taskList: number | null, user: number | null }
    | { type: "property", id: number }
    | { type: "propertyRoute", id: number };

function positiveId(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** The choice in the URL for a view that requires `requiredScope`, or null when it is missing. */
export function taskViewChoiceFromScope(requiredScope: TaskViewRequiredScope, scope: TaskViewScopeParams): TaskViewChoice | null {
  if (requiredScope === "taskList") {
    const taskList = positiveId(scope.userGroup);
    const user = positiveId(scope.user);
    if (taskList === null && user === null) {
      return null;
    }
    return { type: "taskList", taskList, user };
  }
  const id = positiveId(requiredScope === "property" ? scope.property : scope.propertyRoute);
  return id === null ? null : { type: requiredScope, id };
}

/** The list query that opens `viewKey` with `choice`, as in `/tasks?view=task_list&userGroup=3&user=41`. */
export function taskViewChoiceQuery(viewKey: string, choice: TaskViewChoice): Record<string, string> {
  const query: Record<string, string> = { view: viewKey };
  if (choice.type === "taskList") {
    if (choice.taskList !== null) {
      query[TASK_VIEW_SCOPE_QUERY.userGroup] = String(choice.taskList);
    }
    if (choice.user !== null) {
      query[TASK_VIEW_SCOPE_QUERY.user] = String(choice.user);
    }
    return query;
  }
  query[TASK_VIEW_SCOPE_QUERY[choice.type]] = String(choice.id);
  return query;
}

/**
 * The scope of a default view for this choice: `task_list@taskList:3,user:41`, `task_list@user:41`
 * for a user without a department, `per_property@property:12`, `walk_route@propertyRoute:5`.
 */
export function taskViewChoiceToDefaultScope(choice: TaskViewChoice): DefaultViewScope | null {
  if (choice.type !== "taskList") {
    return { type: choice.type, id: choice.id };
  }
  if (choice.taskList !== null) {
    return { type: "taskList", id: choice.taskList, user: choice.user };
  }
  return choice.user === null ? null : { type: "user", id: choice.user };
}

export function taskViewChoiceToTreeSelection(choice: TaskViewChoice | null): TaskListScopeSelection | null {
  if (choice?.type !== "taskList") {
    return null;
  }
  return { taskList: choice.taskList, user: choice.user };
}

export function treeSelectionToTaskViewChoice(selection: TaskListScopeSelection): TaskViewChoice {
  return { type: "taskList", taskList: selection.taskList, user: selection.user };
}
